import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { isDevMode } from '@angular/core';
import { Observable, from, of, switchMap, Subject } from 'rxjs';
import { catchError, bufferTime, filter } from 'rxjs/operators';

import { TranslateLoader } from './translate.loader';
import { mergeDeep } from './util';

declare const require: any;
declare const process: any;

const fs = (() => {
  try {
    return require('fs');
  } catch (e) {
    return null;
  }
})();

const path = (() => {
  try {
    return require('path');
  } catch (e) {
    return null;
  }
})();

export class CustomLoader implements TranslateLoader {
  private environment: any = {};

  private static batchSubject = new Subject<{
    lang: string;
    path: string;
    http: HttpClient;
    resolve: (data: any) => void;
    reject: (err: any) => void;
  }>();
  private static isBatcherInitialized = false;

  constructor(
    private http: HttpClient,
    public folder: string = '',
    private platformId: Object | null = null,
    private environmentService: any = null,
  ) {
    try {
      this.environment = this.environmentService?.environment || {};
    } catch (e) {
      this.environment = {};
    }
  }

  async getTranslationFile(translationPath: string): Promise<any> {
    try {
      const cleanPath = translationPath.replace(/^\/+|\/+$/g, '');

      const candidates: string[] = [];
      candidates.push(`assets/translate/${cleanPath}`);

      for (const fullPath of candidates) {
        try {
          const response = await fetch(fullPath);
          if (!response.ok) {
            continue;
          }
          return await response.json();
        } catch {
          continue;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  private processTranslationFile(filePath: string): { success: boolean; data: any } {
    try {
      if (fs.existsSync(filePath)) {
        const data = fs.readFileSync(filePath, 'utf8');
        return { success: true, data: JSON.parse(data) };
      }
    } catch {
      return { success: false, data: {} };
    }
    return { success: false, data: {} };
  }

  private isServer(): boolean {
    if (this.platformId) {
      return isPlatformServer(this.platformId);
    }
    return typeof window === 'undefined';
  }

  private async loadTranslations(lang: string): Promise<any> {
    if (!this.isServer() || !fs || !path) {
      if (!lang) return {};

      const translationPath = `${this.folder || ''}${lang}.i18n.json`;
      try {
        const translations = await this.getTranslationFile(translationPath);
        if (translations) return translations;
      } catch {}
      return {};
    }

    let jsonData = {};
    const possiblePaths = [
      path.join(process.cwd(), 'translate'),
      path.join(process.cwd(), 'dist/apps/core/browser/assets/translate'),
      path.join(process.cwd(), 'dist/apps/domain/browser/assets/translate'),
      path.join(process.cwd(), 'browser/assets/translate'),
      path.join(process.cwd(), '../browser/assets/translate'),
    ].filter(Boolean);

    try {
      for (const basePath of possiblePaths) {
        if (!basePath) continue;

        let assetsFolder = basePath;
        if (this.folder) {
          if (basePath.includes('translate')) {
            const cleanBasePath = basePath.replace(/[\\\/]+$/, '');
            const cleanFolder = this.folder.replace(/^[\\\/]+/, '');
            assetsFolder = path.join(cleanBasePath, cleanFolder);
          } else {
            assetsFolder = path.join(basePath, this.folder);
          }
        }

        assetsFolder = path.normalize(assetsFolder);
        if (!fs.existsSync(assetsFolder)) continue;

        const directPath = path.join(assetsFolder, `${lang}.i18n.json`);
        const directResult = this.processTranslationFile(directPath);
        if (directResult.success) {
          jsonData = { ...jsonData, ...directResult.data };
        }

        try {
          const files = fs.readdirSync(assetsFolder, { withFileTypes: true });
          for (const file of files) {
            if (file.isDirectory()) {
              const nestedPath = path.join(assetsFolder, file.name, `${lang}.i18n.json`);
              const nestedResult = this.processTranslationFile(nestedPath);
              if (nestedResult.success) {
                jsonData = { ...jsonData, ...nestedResult.data };
              }
            }
          }
        } catch {}
      }
    } catch {}

    return jsonData;
  }

  private static expandDotKeys(obj: any): any {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return obj;
    }

    const result: Record<string, any> = {};

    for (const key of Object.keys(obj)) {
      const value = obj[key];

      if (key.includes('.') && typeof value === 'string') {
        const parts = key.split('.');
        let current = result;

        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!current[part] || typeof current[part] !== 'object') {
            current[part] = {};
          }
          current = current[part];
        }

        current[parts[parts.length - 1]] = value;
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        result[key] = this.expandDotKeys(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  private static initBatcher() {
    if (this.isBatcherInitialized) return;
    this.isBatcherInitialized = true;

    this.batchSubject
      .pipe(
        bufferTime(2000), // Wait a few seconds to collect paths
        filter((batch) => batch.length > 0),
      )
      .subscribe((batch) => {
        // Group by language and baseUrl
        const perLangAndUrl = new Map<string, typeof batch>();
        batch.forEach((item) => {
          const baseUrl = (item as any).baseUrl || 'https://coreapi.offconon.com';
          const key = `${item.lang}|${baseUrl}`;
          const list = perLangAndUrl.get(key) || [];
          list.push(item);
          perLangAndUrl.set(key, list);
        });

        perLangAndUrl.forEach((items, key) => {
          const [lang, baseUrl] = key.split('|');
          const paths = Array.from(new Set(items.map((i) => i.path)));
          const http = items[0].http;

          const apiUrl = `${baseUrl}/api/search/translation_group_json`;

          let params = new HttpParams().set('language', lang);
          paths.forEach((p) => {
            params = params.append('file_path', p);
          });

          http
            .get<Record<string, any>>(apiUrl, { params })
            .pipe(catchError(() => of({})))
            .subscribe({
              next: (res) => {
                const expanded = res && Object.keys(res).length > 0 ? this.expandDotKeys(res) : {};
                items.forEach((item) => item.resolve(expanded));
              },
              error: () => {
                items.forEach((item) => item.resolve({}));
              },
            });
        });
      });
  }

  private getRealtimeTranslations(lang: string): Observable<Record<string, any>> {
    if (!isDevMode() || this.isServer() || this.environmentService?.environment?.production) {
      return of({});
    }

    const cleanFolder = (this.folder ?? '').replace(/^\/+|\/+$/g, '').replace(/[\/]+$/g, '');
    if (!cleanFolder) {
      return of({});
    }

    CustomLoader.initBatcher();

    return new Observable((observer) => {
      CustomLoader.batchSubject.next({
        lang: lang,
        path: cleanFolder,
        http: this.http,
        baseUrl: this.environment?.coreApi,
        resolve: (data: any) => {
          observer.next(data);
          observer.complete();
        },
        reject: (_err: any) => {
          observer.next({});
          observer.complete();
        },
      } as any);
    });
  }

  getTranslation(lang: string): Observable<Record<string, any>> {
    if (!lang) {
      return of({});
    }

    return from(this.loadTranslations(lang)).pipe(
      switchMap((translations: Record<string, any>) => {
        if (isDevMode() && !this.isServer()) {
          return this.getRealtimeTranslations(lang).pipe(
            switchMap((realtimeTranslations) => {
              if (realtimeTranslations && Object.keys(realtimeTranslations).length > 0) {
                return of(mergeDeep(translations ?? {}, realtimeTranslations));
              }
              return of(translations ?? {});
            }),
          );
        }

        if (Object.keys(translations).length > 0) {
          return of(translations);
        }

        if (lang !== 'en' && this.environmentService?.environment?.production) {
          return from(this.loadTranslations('en'));
        }

        return of({});
      }),
      catchError(() => of({})),
    );
  }
}
